import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { salesCredentialsAPI, usersAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Loader2, 
  Key,
  Eye,
  EyeOff,
  Copy,
  ExternalLink,
  Lock,
  User,
  Users
} from 'lucide-react';

const Sales = () => {
  const { user } = useAuth();
  const [credentials, setCredentials] = useState([]);
  const [vendedores, setVendedores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalAberto, setModalAberto] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [credentialEditando, setCredentialEditando] = useState(null);
  const [senhasVisiveis, setSenhasVisiveis] = useState({});
  
  // Formulário
  const [formData, setFormData] = useState({
    nome: '',
    login: '',
    senha: '',
    url: '',
    observacao: '',
    vendedor_id: '',
    vendedor_nome: ''
  });

  const isAdmin = user?.perfil === 'ADMIN';

  useEffect(() => {
    carregarDados();
  }, []);

  const carregarDados = async () => {
    setLoading(true);
    try {
      const [credentialsRes, vendedoresRes] = await Promise.all([
        salesCredentialsAPI.listar(),
        isAdmin ? usersAPI.list() : Promise.resolve({ data: [] })
      ]);
      
      setCredentials(credentialsRes.data);
      
      if (isAdmin) {
        // Filtrar apenas vendedores ativos
        setVendedores(vendedoresRes.data.filter(u => 
          (u.perfil === 'VENDEDOR' || u.perfil === 'ADMIN' || u.perfil === 'BKO') && u.ativo !== false
        ));
      }
    } catch (error) {
      console.error('Erro ao carregar:', error);
      toast.error('Erro ao carregar credenciais');
    } finally {
      setLoading(false);
    }
  };

  const abrirModalNovo = () => {
    setCredentialEditando(null);
    setFormData({
      nome: '',
      login: '',
      senha: '',
      url: '',
      observacao: '',
      vendedor_id: '',
      vendedor_nome: ''
    });
    setModalAberto(true);
  };

  const abrirModalEditar = (credential) => {
    setCredentialEditando(credential);
    setFormData({
      nome: credential.nome,
      login: credential.login,
      senha: credential.senha,
      url: credential.url || '',
      observacao: credential.observacao || '',
      vendedor_id: credential.vendedor_id || '',
      vendedor_nome: credential.vendedor_nome || ''
    });
    setModalAberto(true);
  };

  const handleVendedorChange = (vendedorId) => {
    if (vendedorId === 'geral') {
      setFormData({ ...formData, vendedor_id: '', vendedor_nome: '' });
    } else {
      const vendedor = vendedores.find(v => v.id === vendedorId);
      setFormData({ 
        ...formData, 
        vendedor_id: vendedorId, 
        vendedor_nome: vendedor?.nome || '' 
      });
    }
  };

  const handleSalvar = async () => {
    if (!formData.nome.trim() || !formData.login.trim() || !formData.senha.trim()) {
      toast.error('Preencha nome, login e senha');
      return;
    }

    setSalvando(true);
    try {
      const dataToSend = {
        ...formData,
        vendedor_id: formData.vendedor_id || null,
        vendedor_nome: formData.vendedor_nome || null
      };

      if (credentialEditando) {
        await salesCredentialsAPI.atualizar(credentialEditando.id, dataToSend);
        toast.success('Credencial atualizada!');
      } else {
        await salesCredentialsAPI.criar(dataToSend);
        toast.success('Credencial criada!');
      }
      setModalAberto(false);
      carregarDados();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar');
    } finally {
      setSalvando(false);
    }
  };

  const handleExcluir = async (credential) => {
    if (!window.confirm(`Excluir credencial "${credential.nome}"?`)) return;

    try {
      await salesCredentialsAPI.excluir(credential.id);
      toast.success('Credencial excluída!');
      carregarDados();
    } catch (error) {
      toast.error('Erro ao excluir');
    }
  };

  const toggleSenhaVisivel = (id) => {
    setSenhasVisiveis(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const copiarParaClipboard = (texto, tipo) => {
    navigator.clipboard.writeText(texto);
    toast.success(`${tipo} copiado!`);
  };

  // Agrupar credenciais por vendedor
  const credenciaisAgrupadas = () => {
    const grupos = {};
    
    // Primeiro, credenciais gerais (sem vendedor)
    grupos['geral'] = {
      nome: 'Credenciais Gerais',
      credenciais: credentials.filter(c => !c.vendedor_id)
    };
    
    // Depois, por vendedor
    credentials.forEach(c => {
      if (c.vendedor_id && c.vendedor_nome) {
        if (!grupos[c.vendedor_id]) {
          grupos[c.vendedor_id] = {
            nome: c.vendedor_nome,
            credenciais: []
          };
        }
        grupos[c.vendedor_id].credenciais.push(c);
      }
    });
    
    return grupos;
  };

  const renderCredentialCard = (credential) => (
    <Card key={credential.id} className="border-gray-200 hover:shadow-md transition-shadow">
      <CardContent className="p-4">
        {/* Nome/Título */}
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-gray-900 text-lg">{credential.nome}</h3>
          {isAdmin && (
            <div className="flex gap-1">
              <Button
                size="sm"
                variant="ghost"
                onClick={() => abrirModalEditar(credential)}
              >
                <Edit className="h-4 w-4 text-blue-600" />
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => handleExcluir(credential)}
              >
                <Trash2 className="h-4 w-4 text-red-600" />
              </Button>
            </div>
          )}
        </div>

        {/* Login */}
        <div className="mb-2">
          <Label className="text-xs text-gray-500">Login</Label>
          <div className="flex items-center gap-2">
            <span className="text-sm font-mono bg-gray-100 px-2 py-1 rounded flex-1">
              {credential.login}
            </span>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => copiarParaClipboard(credential.login, 'Login')}
            >
              <Copy className="h-4 w-4 text-gray-500" />
            </Button>
          </div>
        </div>

        {/* Senha */}
        <div className="mb-2">
          <Label className="text-xs text-gray-500">Senha</Label>
          <div className="flex items-center gap-2">
            <span className="text-sm font-mono bg-gray-100 px-2 py-1 rounded flex-1">
              {senhasVisiveis[credential.id] ? credential.senha : '••••••••'}
            </span>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => toggleSenhaVisivel(credential.id)}
            >
              {senhasVisiveis[credential.id] ? (
                <EyeOff className="h-4 w-4 text-gray-500" />
              ) : (
                <Eye className="h-4 w-4 text-gray-500" />
              )}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => copiarParaClipboard(credential.senha, 'Senha')}
            >
              <Copy className="h-4 w-4 text-gray-500" />
            </Button>
          </div>
        </div>

        {/* URL */}
        {credential.url && (
          <div className="mb-2">
            <Label className="text-xs text-gray-500">URL</Label>
            <div className="flex items-center gap-2">
              <a 
                href={credential.url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-sm text-blue-600 hover:underline flex items-center gap-1 truncate"
              >
                {credential.url}
                <ExternalLink className="h-3 w-3" />
              </a>
            </div>
          </div>
        )}

        {/* Observação */}
        {credential.observacao && (
          <div className="mb-2">
            <Label className="text-xs text-gray-500">Observação</Label>
            <p className="text-sm text-gray-600">{credential.observacao}</p>
          </div>
        )}

        {/* Rodapé */}
        <div className="mt-3 pt-2 border-t text-xs text-gray-400">
          Criado por {credential.criado_por}
        </div>
      </CardContent>
    </Card>
  );

  const grupos = credenciaisAgrupadas();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Key className="h-7 w-7 text-red-600" />
            Sales - Credenciais
          </h1>
          <p className="text-gray-500 text-sm mt-1">
            {isAdmin ? 'Gerencie logins e senhas dos sistemas de vendas' : 'Visualize os acessos dos sistemas de vendas'}
          </p>
        </div>
        
        {isAdmin && (
          <Button 
            className="bg-red-600 hover:bg-red-700"
            onClick={abrirModalNovo}
          >
            <Plus className="mr-2 h-4 w-4" />
            Nova Credencial
          </Button>
        )}
      </div>

      {/* Aviso para não-admins */}
      {!isAdmin && (
        <Card className="bg-yellow-50 border-yellow-200">
          <CardContent className="py-4">
            <div className="flex items-center gap-3">
              <Lock className="h-5 w-5 text-yellow-600" />
              <p className="text-sm text-yellow-800">
                Você tem permissão apenas para <strong>visualizar</strong> as credenciais. Entre em contato com o administrador para alterações.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Conteúdo */}
      {loading ? (
        <div className="text-center py-12">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-red-600" />
          <p className="text-gray-500 mt-2">Carregando credenciais...</p>
        </div>
      ) : credentials.length === 0 ? (
        <Card>
          <CardContent className="py-12">
            <div className="text-center">
              <Key className="h-12 w-12 mx-auto text-gray-400 mb-3" />
              <p className="text-gray-500">Nenhuma credencial cadastrada</p>
              {isAdmin && (
                <Button variant="outline" className="mt-4" onClick={abrirModalNovo}>
                  <Plus className="mr-2 h-4 w-4" />
                  Adicionar primeira credencial
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {/* Credenciais Gerais */}
          {grupos['geral']?.credenciais.length > 0 && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Users className="h-5 w-5 text-red-600" />
                  Credenciais Gerais
                  <span className="text-sm font-normal text-gray-500">
                    ({grupos['geral'].credenciais.length})
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                  {grupos['geral'].credenciais.map(renderCredentialCard)}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Credenciais por Vendedor */}
          {Object.entries(grupos)
            .filter(([key]) => key !== 'geral')
            .sort(([, a], [, b]) => a.nome.localeCompare(b.nome))
            .map(([vendedorId, grupo]) => (
              <Card key={vendedorId}>
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <User className="h-5 w-5 text-blue-600" />
                    {grupo.nome}
                    <span className="text-sm font-normal text-gray-500">
                      ({grupo.credenciais.length})
                    </span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                    {grupo.credenciais.map(renderCredentialCard)}
                  </div>
                </CardContent>
              </Card>
            ))}
        </div>
      )}

      {/* Modal Criar/Editar */}
      <Dialog open={modalAberto} onOpenChange={setModalAberto}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="h-5 w-5 text-red-600" />
              {credentialEditando ? 'Editar Credencial' : 'Nova Credencial'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Vendedor */}
            <div>
              <Label>Vendedor</Label>
              <Select 
                value={formData.vendedor_id || 'geral'} 
                onValueChange={handleVendedorChange}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Selecione o vendedor" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="geral">
                    <span className="flex items-center gap-2">
                      <Users className="h-4 w-4" />
                      Geral (Todos)
                    </span>
                  </SelectItem>
                  {vendedores.map(v => (
                    <SelectItem key={v.id} value={v.id}>
                      <span className="flex items-center gap-2">
                        <User className="h-4 w-4" />
                        {v.nome}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500 mt-1">
                Selecione "Geral" para credenciais visíveis para todos
              </p>
            </div>

            <div>
              <Label>Nome do Sistema <span className="text-red-500">*</span></Label>
              <Input
                value={formData.nome}
                onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                placeholder="Ex: Portal Claro, Sistema NET..."
                className="mt-1"
              />
            </div>

            <div>
              <Label>Login <span className="text-red-500">*</span></Label>
              <Input
                value={formData.login}
                onChange={(e) => setFormData({ ...formData, login: e.target.value })}
                placeholder="Digite o login"
                className="mt-1"
              />
            </div>

            <div>
              <Label>Senha <span className="text-red-500">*</span></Label>
              <Input
                value={formData.senha}
                onChange={(e) => setFormData({ ...formData, senha: e.target.value })}
                placeholder="Digite a senha"
                className="mt-1"
              />
            </div>

            <div>
              <Label>URL (opcional)</Label>
              <Input
                value={formData.url}
                onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                placeholder="https://..."
                className="mt-1"
              />
            </div>

            <div>
              <Label>Observação (opcional)</Label>
              <Textarea
                value={formData.observacao}
                onChange={(e) => setFormData({ ...formData, observacao: e.target.value })}
                placeholder="Informações adicionais..."
                className="mt-1"
                rows={2}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setModalAberto(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleSalvar} 
              disabled={salvando}
              className="bg-red-600 hover:bg-red-700"
            >
              {salvando ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Key className="mr-2 h-4 w-4" />}
              {credentialEditando ? 'Salvar' : 'Criar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Sales;
